import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  X, 
  Save, 
  ArrowUp, 
  ArrowDown,
  Package,
  Clock,
  CheckCircle
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import debounce from 'lodash/debounce';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const ORDER_STATUSES = [
  'Pending',
  'Complete',
  'In Progress',
  'Cancelled',
  'On Hold',
  'On Order',
];

const SHIPPING_STATUSES = [
  'Waiting For Collection',
  'Shipped',
  'Delayed',
];

const StaffOrderManagement = () => {
  // ?search=<order_number> (e.g. from the staff dashboard) pre-fills the search
  const initialSearch = new URLSearchParams(useLocation().search).get('search') || '';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [filterCompany, setFilterCompany] = useState('');
  const [timeRange, setTimeRange] = useState('All Time');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [accounts, setAccounts] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [limit, setLimit] = useState(30);
  const [totalOrders, setTotalOrders] = useState(0);

  useEffect(() => {
    fetchOrders();
    fetchAccounts();
  }, [timeRange, sortField, sortDirection, filterCompany, debouncedSearchTerm, limit]);

  const fetchAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, company_name')
        .order('company_name');

      if (error) throw error;
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showError(error, 'Failed to fetch accounts');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('orders')
        .select(
          `
          id,
          order_id,
          order_number,
          status,
          created_at,
          cart_items,
          user_id,
          notes,
          mhs_reference,
          shipping_status,
          connote,
          accounts:user_id (
            id,
            company_name,
            mhs_account_number
          )
        `,
          { count: 'exact' }
        )
        .order(sortField, { ascending: sortDirection === 'asc' })
        .limit(limit);

      if (filterCompany) {
        query = query.eq('accounts.company_name', filterCompany);
      }

      if (debouncedSearchTerm) {
        query = query.or(
          `order_number.ilike.%${debouncedSearchTerm}%,mhs_reference.ilike.%${debouncedSearchTerm}%,order_id.ilike.%${debouncedSearchTerm}%`
        );
      }

      if (timeRange === 'Current Month') {
        const startDate = startOfMonth(new Date());
        const endDate = endOfMonth(new Date());
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      } else if (timeRange === 'Last Month') {
        const lastMonthDate = new Date();
        lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
        const startDate = startOfMonth(lastMonthDate);
        const endDate = endOfMonth(lastMonthDate);
        query = query
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
      }

      const { data, error, count } = await query;

      if (error) throw error;

      setOrders(data || []);
      setTotalOrders(count || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showError(error, 'Failed to fetch orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder({ ...order });
    setIsEditModalOpen(true);
  };

  const handleViewOrder = (order) => {
    setViewingOrder({ ...order });
    setIsViewModalOpen(true);
  };

  const handleSaveOrder = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .update({
          status: editingOrder.status,
          notes: editingOrder.notes,
          mhs_reference: editingOrder.mhs_reference,
          shipping_status: editingOrder.shipping_status,
          connote: editingOrder.connote,
        })
        .eq('id', editingOrder.id)
        .select(
          `
          id,
          order_number,
          status,
          created_at,
          cart_items,
          user_id,
          notes,
          mhs_reference,
          shipping_status,
          connote,
          accounts:user_id (
            id,
            company_name,
            mhs_account_number
          )
        `
        );

      if (error) throw error;

      // Create a notification for the customer
      await supabase.from('notifications').insert({
        user_id: editingOrder.user_id,
        message: `Order #${editingOrder.order_number} has been updated. New status: ${editingOrder.status}`,
        read: false,
      });

      setOrders(
        orders.map((order) => (order.id === editingOrder.id ? data[0] : order))
      );
      setEditingOrder(null);
      setIsEditModalOpen(false);
      showNotification('Order updated successfully', 'success');
    } catch (error) {
      console.error('Error updating order:', error);
      showError(error, 'Failed to update order');
    }
  };

  const handleCancelEdit = () => {
    setEditingOrder(null);
    setIsEditModalOpen(false);
  };

  const handleCloseView = () => {
    setViewingOrder(null);
    setIsViewModalOpen(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const loadMoreOrders = () => {
    setLimit(limit + 30);
  };

  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debouncedSetSearchTerm(e.target.value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Complete':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      case 'On Hold':
        return 'bg-orange-100 text-orange-800';
      case 'On Order':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Orders"
        description="View and manage all customer orders, update statuses, and track shipments."
      />

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Search & Filter</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
            />
          </div>

          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
          >
            <option value="All Time">All Time</option>
            <option value="Current Month">Current Month</option>
            <option value="Last Month">Last Month</option>
          </select>

          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
          >
            <option value="">All Companies</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.company_name}>
                {account.company_name}
              </option>
            ))}
          </select>

          <div className="flex items-center text-sm text-gray-600">
            <Package className="w-4 h-4 mr-1" />
            {totalOrders} total orders
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
                <th
                  onClick={() => handleSort('order_id')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    Order ID
                    {sortField === 'order_id' && (
                      sortDirection === 'asc' ? 
                        <ArrowUp className="ml-1 h-4 w-4" /> : 
                        <ArrowDown className="ml-1 h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('order_number')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    Order #
                    {sortField === 'order_number' && (
                      sortDirection === 'asc' ? 
                        <ArrowUp className="ml-1 h-4 w-4" /> : 
                        <ArrowDown className="ml-1 h-4 w-4" />
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('created_at')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    Date
                    {sortField === 'created_at' && (
                      sortDirection === 'asc' ? 
                        <ArrowUp className="ml-1 h-4 w-4" /> : 
                        <ArrowDown className="ml-1 h-4 w-4" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th
                  onClick={() => handleSort('status')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    Status
                    {sortField === 'status' && (
                      sortDirection === 'asc' ? 
                        <ArrowUp className="ml-1 h-4 w-4" /> : 
                        <ArrowDown className="ml-1 h-4 w-4" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="animate-pulse h-4 bg-gray-200 rounded"></div>
                    </td>
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    No orders found matching your criteria.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Order"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditOrder(order)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit Order"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.order_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.order_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(order.created_at), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.accounts?.company_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${order.cart_items?.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More */}
        {orders.length < totalOrders && !loading && (
          <div className="bg-gray-50 px-6 py-3 flex justify-center">
            <Button onClick={loadMoreOrders}>
              Load More Orders
            </Button>
          </div>
        )}
      </div>

      {/* View Order Modal */}
      {isViewModalOpen && viewingOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Order Details</h3>
                  <button
                    onClick={handleCloseView}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-700">Order ID:</span>
                      <p className="text-gray-900">{viewingOrder.order_id}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Order Number:</span>
                      <p className="text-gray-900">{viewingOrder.order_number}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Date:</span>
                      <p className="text-gray-900">{format(new Date(viewingOrder.created_at), 'MMM dd, yyyy')}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Company:</span>
                      <p className="text-gray-900">{viewingOrder.accounts?.company_name}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(viewingOrder.status)}`}>
                        {viewingOrder.status}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Shipping Status:</span>
                      <p className="text-gray-900">{viewingOrder.shipping_status || 'Not set'}</p>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">MHS Reference:</span>
                    <p className="text-gray-900">{viewingOrder.mhs_reference || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">ConNote:</span>
                    <p className="text-gray-900">{viewingOrder.connote || 'Not set'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Notes:</span>
                    <p className="text-gray-900">{viewingOrder.notes || 'No notes'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total:</span>
                    <p className="text-gray-900 font-semibold">
                      ${viewingOrder.cart_items?.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:w-auto sm:text-sm"
                  onClick={handleCloseView}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {isEditModalOpen && editingOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Edit Order #{editingOrder.order_number}
                  </h3>
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={editingOrder.status}
                      onChange={(e) => setEditingOrder({ ...editingOrder, status: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    >
                      {ORDER_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Shipping Status</label>
                    <select
                      value={editingOrder.shipping_status || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, shipping_status: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    >
                      <option value="">Select shipping status</option>
                      {SHIPPING_STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">MHS Reference</label>
                    <input
                      type="text"
                      value={editingOrder.mhs_reference || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, mhs_reference: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ConNote</label>
                    <input
                      type="text"
                      value={editingOrder.connote || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, connote: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      value={editingOrder.notes || ''}
                      onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value })}
                      rows="3"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-black hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleSaveOrder}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffOrderManagement;
